import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Message } from "primereact/message";

import { previewTechnicalNames } from "../../../../services/onboarding/prospects";

interface AssignTechnicalNamesDialogProps {
  visible: boolean;
  stationCount: number;
  existingNames: string[];
  onHide: () => void;
  onAssign: (customNames?: string[]) => Promise<boolean>;
}

/**
 * US3: preview the next auto-generated technical names, or supply a custom list
 * of exactly stationCount names (FR-017/FR-018).
 *
 * The count is WALLBOXES, not charging points: eRound holds one technical name
 * per charging station, however many sockets it provides (CWA-154 finding 1).
 */
const AssignTechnicalNamesDialog: React.FC<AssignTechnicalNamesDialogProps> = ({
  visible,
  stationCount,
  existingNames,
  onHide,
  onAssign,
}) => {
  const [preview, setPreview] = useState<string[]>([]);
  const [lastAssigned, setLastAssigned] = useState<string | null>(null);
  const [useCustom, setUseCustom] = useState(false);
  const [custom, setCustom] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setUseCustom(false);
    setError(null);
    setCustom(Array.from({ length: stationCount }, () => ""));
    previewTechnicalNames(stationCount)
      .then((res) => {
        setPreview(res?.next_names ?? []);
        setLastAssigned(res?.last_assigned ?? null);
      })
      .catch(() => {
        setPreview([]);
        setLastAssigned(null);
      });
  }, [visible, stationCount]);

  const setCustomAt = (i: number, value: string) => {
    setCustom((prev) => prev.map((v, idx) => (idx === i ? value : v)));
    setError(null);
  };

  const submit = async () => {
    setError(null);
    let payload: string[] | undefined;
    if (useCustom) {
      const trimmed = custom.map((c) => c.trim());
      if (trimmed.some((c) => !c) || trimmed.length !== stationCount) {
        setError(
          `Please provide exactly ${stationCount} station ID${
            stationCount > 1 ? "s" : ""
          }.`
        );
        return;
      }
      payload = trimmed;
    }
    setSubmitting(true);
    try {
      const ok = await onAssign(payload);
      if (ok) onHide();
    } catch (e: any) {
      if (e?.response?.data?.error === "TECHNICAL_NAME_COUNT_MISMATCH") {
        const expected = e.response.data.expected ?? stationCount;
        setError(`The number of station IDs must be exactly ${expected}.`);
      } else {
        setError("Could not assign station IDs. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button
        label="Assign"
        icon="pi pi-check"
        onClick={submit}
        loading={submitting}
      />
    </div>
  );

  return (
    <Dialog
      header="Assign station IDs"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "32rem" }}
    >
      {existingNames?.length > 0 && (
        <Message
          severity="info"
          className="w-full mb-3"
          text={`Already assigned: ${existingNames.join(", ")}`}
        />
      )}

      <p className="mt-0">
        This prospect has {stationCount} wallbox{stationCount === 1 ? "" : "es"}.
      </p>

      {!useCustom && (
        <div className="mb-3">
          <div className="text-500 text-sm mb-1">Station IDs to be assigned</div>
          <div className="font-medium">
            {preview.length ? preview.join(", ") : "—"}
          </div>
          {lastAssigned && (
            <div className="text-500 text-sm mt-1">
              Last assigned overall: {lastAssigned}
            </div>
          )}
        </div>
      )}

      <div className="flex align-items-center gap-2 mb-3">
        <Checkbox
          inputId="use-custom"
          checked={useCustom}
          onChange={(e) => setUseCustom(!!e.checked)}
        />
        <label htmlFor="use-custom">Enter custom IDs instead</label>
      </div>

      {useCustom && (
        <div className="flex flex-column gap-2">
          {custom.map((value, i) => (
            <InputText
              key={i}
              value={value}
              placeholder={`Name ${i + 1}`}
              onChange={(e) => setCustomAt(i, e.target.value)}
            />
          ))}
        </div>
      )}

      {error && (
        <Message severity="error" className="w-full mt-3" text={error} />
      )}
    </Dialog>
  );
};

export default AssignTechnicalNamesDialog;
